import { v1 as uuid } from 'uuid';
import { isEmpty, isUndefined, find, isNil } from 'lodash';

import {
  GROUP_AB_TEST,
  GROUP_CONJOINT,
  GROUP_VAN_WESTENDORP,
  GROUP_MAXDIFF,
  GROUP_LOOP_MERGE,
} from 'data/nodeTypes';
import {
  isAbGroup,
  isLoopAndMergeGroup,
  isVanWestendorpGroup,
} from 'utils/nodeTypes';
import {
  AB_DEFAULT_REPETITIONS,
  CONJOINT_DEFAULT_REPETITIONS,
  CONJOINT_DEFAULT_ALTERNATIVES,
  VAN_WESTENDORP_DEFAULT_REPETITIONS,
  LOOP_AND_MERGE_DEFAULT_REPETITIONS,
} from 'containers/QuestionnairePage/NodeSettings/constants';

import { SHUFFLE_NONE } from 'components/DropdownShuffle/constants';

import { CONJOINT_ATTRIBUTE_TYPES } from './Conjoint/constants';
import { BIPOLAR_STYLES } from '../GenericQuestion/BipolarQuestion/constants';
import {
  MATRIX_OPEN_ENDED_STYLES,
  MATRIX_OPEN_ENDED_INPUT_SIZE,
  MATRIX_OPEN_ENDED_SIGN_PLACEMENT,
  MATRIX_CONSTANT_SUM_TYPES,
} from '../GenericQuestion/MatrixOpenEndedQuestion/constants';
import { LOOP_AND_MERGE_ATTRIBUTE_TYPE } from './constants';

export const getConceptIds = (abTestNode) => {
  if (!abTestNode || !abTestNode.concepts) {
    return [];
  }

  return abTestNode.concepts.map(({ refId }) => refId);
};

export const getAttributeIds = (abTestNode) => {
  if (!abTestNode || !abTestNode.attributes) {
    return [];
  }

  return abTestNode.attributes.map(({ refId }) => refId);
};

export const getAttributeValueIds = (groupNode, attributeRefId) => {
  if (!groupNode || !groupNode.attributes) {
    return [];
  }

  const attribute = find(groupNode.attributes, ['refId', attributeRefId]);
  if (attribute) {
    return attribute.values.map(({ refId }) => refId);
  }

  return [];
};

export const getEmptyAttributeValue = () => ({
  refId: uuid(),
});

export const getAttributeValueWithName = (name) => ({
  ...getEmptyAttributeValue(),
  name,
});

export const getLoopAndMergeConceptName = (index) => `L${index + 1}`;
export const getLoopAndMergeAttributeName = (index) => `Variable ${index + 1}`;

export const getEmptyAttribute = ({ groupType, concepts = 2 }, rest = {}) => {
  const partialUpdates = {
    refId: uuid(),
  };

  if (isAbGroup(groupType) || isVanWestendorpGroup(groupType)) {
    partialUpdates.values = [...Array(concepts)].map(getEmptyAttributeValue);
  } else if (isLoopAndMergeGroup(groupType)) {
    partialUpdates.values = [...Array(concepts)].map(getEmptyAttributeValue);
  } else {
    partialUpdates.values = [
      getEmptyAttributeValue(),
      getEmptyAttributeValue(),
    ];
  }

  return {
    ...rest,
    ...partialUpdates,
  };
};

export const getLoopAndMergeFromQuestionAttribute = (values) => ({
  refId: uuid(),
  values: values.map(
    ({
      text,
      isCarriedForward,
      originalAnswerRefId,
      originalScorepointRefId,
      questionRefId,
      refId,
    }) => ({
      text,
      isCarriedForward,
      originalAnswerRefId,
      originalScorepointRefId,
      questionRefId,
      refId,
    }),
  ),
  name: 'Source question',
  type: LOOP_AND_MERGE_ATTRIBUTE_TYPE,
});

export const isLoopAndMergeAttributeBasedOnQuestion = (attribute) =>
  attribute.type === LOOP_AND_MERGE_ATTRIBUTE_TYPE;

export const getEmptyConcept = (name = '') => ({
  refId: uuid(),
  name,
});

export const getTransformedABTestConcepts = () => [
  getEmptyConcept(),
  getEmptyConcept(),
];

export const getTransformedABTestAttributes = (attributes = 1) =>
  [...Array(attributes)].map(() =>
    getEmptyAttribute({ groupType: GROUP_AB_TEST, concepts: 2 }, { name: '' }),
  );

export const getDefaultABTestProperties = () => ({
  repetitions: AB_DEFAULT_REPETITIONS,
});

export const getLoopAndMergeAttributes = (
  attributesToCreate = 2,
  conceptsToCreate = 3,
) =>
  [...Array(attributesToCreate)].map((_, index) =>
    getEmptyAttribute(
      {
        groupType: GROUP_LOOP_MERGE,
        concepts: conceptsToCreate,
      },
      { name: getLoopAndMergeAttributeName(index) },
    ),
  );

export const getDefaultLoopAndMergeAttributes = (questionType) => {
  if (questionType === GROUP_LOOP_MERGE) return getLoopAndMergeAttributes();
  return [];
};

export const getLoopAndMergeProperties = ({
  repetitions = LOOP_AND_MERGE_DEFAULT_REPETITIONS,
  shuffle = SHUFFLE_NONE,
  userDefinedRepetitions = false,
} = {}) => ({
  repetitions,
  shuffle,
  userDefinedRepetitions,
});

export const getDefaultLoopAndMergeProperties = (questionType) => {
  if (questionType === GROUP_LOOP_MERGE) return getLoopAndMergeProperties();
  return {};
};

export const getLoopAndMergeConceptWithName = (index) =>
  getEmptyConcept(getLoopAndMergeConceptName(index));

export const getLoopAndMergeConcepts = (concepts = 3) =>
  [...Array(concepts)].map((_, index) => getLoopAndMergeConceptWithName(index));

export const getDefaultLoopAndMergeConcepts = (questionType) => {
  if (questionType === GROUP_LOOP_MERGE) return getLoopAndMergeConcepts();
  return {};
};

export const getTransformedVanWestendorpConcepts = () => [getEmptyConcept()];

export const getDefaultBipolarMatrix = () => ({
  matrixAnswers: [],
  columns: [],
  matrixExtraLabels: undefined,
  openEndedConfig: {
    refId: uuid(),
    style: BIPOLAR_STYLES.HORIZONTAL,
  },
});

export const getDefaultOpenEndedMatrix = () => ({
  matrixAnswers: [],
  columns: [],
  answerColumnValueTypes: [],
  openEndedConfig: {
    refId: uuid(),
    style: MATRIX_OPEN_ENDED_STYLES.VERTICAL,
    inputSize: MATRIX_OPEN_ENDED_INPUT_SIZE.SMALL,
    signPlacement: MATRIX_OPEN_ENDED_SIGN_PLACEMENT.LEFT,
    sign: null,
  },
  validation: {
    openEndedValidation: {
      refId: uuid(),
      decimals: null,
      min: null,
      max: null,
      minLength: null,
      maxLength: null,
    },
  },
});

export const getDefaultConversationalMatrix = () => ({
  matrixAnswers: [],
  columns: [],
  answerColumnValueTypes: [],
  validation: {},
});

export const getDefaultConstantSumMatrix = () => ({
  matrixAnswers: [],
  columns: [],
  answerColumnValueTypes: [],
  openEndedConfig: {
    refId: uuid(),
    style: MATRIX_OPEN_ENDED_STYLES.VERTICAL,
    inputSize: MATRIX_OPEN_ENDED_INPUT_SIZE.LARGE,
    signPlacement: MATRIX_OPEN_ENDED_SIGN_PLACEMENT.LEFT,
    sign: null,
    gridType: MATRIX_CONSTANT_SUM_TYPES.MUST_TOTAL,
  },
  validation: {
    openEndedValidation: {
      refId: uuid(),
      decimals: null,
      min: null,
      max: null,
      total: 100,
    },
  },
});

export const getDefaultVanWestendorpQuestionMatrix = () => ({
  matrixAnswers: null,
  columns: [
    {
      ...getEmptyAttributeValue(),
    },
  ],
  capabilities: null,
  answerColumnValueTypes: null,
  validation: {
    vanWestendorpValidationDto: {
      ...getEmptyAttributeValue(),
      min: null,
      max: null,
      ordering: 1,
    },
  },
});

export const getTransformedVanWestendorpAttributes = (attributes = 1) =>
  [...Array(attributes)].map((_, index) =>
    getEmptyAttribute(
      { groupType: GROUP_VAN_WESTENDORP, concepts: 1 },
      { name: `Attribute ${index + 1}` },
    ),
  );

export const getDefaultVanWestendorpProperties = () => ({
  repetitions: VAN_WESTENDORP_DEFAULT_REPETITIONS,
});

export const getDefaultConjointProperties = () => ({
  repetitions: CONJOINT_DEFAULT_REPETITIONS,
  choiceSetSize: CONJOINT_DEFAULT_ALTERNATIVES,
  conceptsLabel: '',
  excludedCombinations: [],
  doeStructureVersion: 0,
  lastSuccessDoeVersion: -1,
  // not sure if we need the peristance version anywhere
  // lastGeneratedDoeVersion: -1,
});

export const getDefaultConjointAttributes = () => [
  getEmptyAttribute(
    { groupType: GROUP_CONJOINT },
    { name: '', type: CONJOINT_ATTRIBUTE_TYPES.FEATURE },
  ),
  getEmptyAttribute(
    { groupType: GROUP_CONJOINT },
    { name: '', type: CONJOINT_ATTRIBUTE_TYPES.FEATURE },
  ),
];

export const getDefaultMaxDiffAttributes = () => [
  getEmptyAttribute(
    { groupType: GROUP_MAXDIFF },
    { name: '', type: CONJOINT_ATTRIBUTE_TYPES.FEATURE },
  ),
];

export const getDefaultMaxDiffProperties = () => ({
  ...getDefaultConjointProperties(),
});

export const getDefaultMaxDiffQuestionMatrix = () => ({
  matrixAnswers: null,
  columns: [
    {
      refId: uuid(),
      beforeLabel: 'Best',
      sort: 0,
    },
    {
      refId: uuid(),
      beforeLabel: 'Worst',
      sort: 1,
    },
  ],
  capabilities: null,
  answerColumnValueTypes: null,
  validation: {},
});

export const getDefaultDrillDownQuestionMatrix = () => ({
  matrixAnswers: null,
  columns: [],
  capabilities: null,
  answerColumnValueTypes: null,
  validation: {
    drillDownConfigurationDto: {
      refId: uuid(),
      config: {
        levels: [],
      },
      fileName: '',
    },
  },
});

export const getEmptyConjointAttributeValue = () => ({
  ...getEmptyAttributeValue(),
  isEmpty: true,
});

export const emptyAttributeValuesSelector = (values) =>
  values.filter((value) => value.isEmpty);

export const attachEmptyConjointAttributeValue = (values) => {
  if (!values || values.length === 0) {
    return [getEmptyConjointAttributeValue()];
  }

  return [...values, getEmptyConjointAttributeValue()];
};

export const hasConjointEmptyAttributeValues = (values, attributeType) => {
  const isAttributeTypePrice = attributeType === CONJOINT_ATTRIBUTE_TYPES.PRICE;

  return (
    isEmpty(values) ||
    values.some(
      ({ media, text, numericValue, isEmpty: isEmptyAttributeValue }) => {
        if (isEmptyAttributeValue) {
          return false;
        }

        if (isEmpty(media) && isEmpty(text)) {
          return true;
        }

        if (isAttributeTypePrice && isUndefined(numericValue)) {
          return true;
        }

        return false;
      },
    )
  );
};

export const hasMixedPositiveWithNegativePriceValues = (
  values,
  attributeType,
) => {
  if (attributeType !== CONJOINT_ATTRIBUTE_TYPES.PRICE) {
    return false;
  }

  const valuesSign = values.reduce(
    (accumulator, { numericValue, isEmpty: isEmptyAttributeValue }) => {
      if (!isEmptyAttributeValue && numericValue) {
        accumulator.add(Math.sign(numericValue));
      }

      return accumulator;
    },
    new Set(),
  );

  return valuesSign.size > 1;
};

export const hasSameAttributePriceValues = (values, attributeType) => {
  if (attributeType !== CONJOINT_ATTRIBUTE_TYPES.PRICE) {
    return false;
  }

  const uniqValues = new Set(values.map(({ numericValue }) => numericValue));

  return values.length !== uniqValues.size;
};

export const getConjointAttributeValuesOnAdd = (
  values,
  attributeValueRefId,
) => {
  const currentValueIndex = values.findIndex(
    (value) => value.refId === attributeValueRefId,
  );
  const currentValue = values[currentValueIndex];

  if (currentValue.isEmpty) {
    return {
      shouldUpdate: false,
      newValues: values,
    };
  }

  const newValues = [...values];
  newValues.splice(currentValueIndex + 1, 0, getEmptyAttributeValue());

  return {
    shouldUpdate: true,
    newValues,
  };
};

export const getNumericFromText = (text) =>
  !isNil(text) ? parseFloat(text) : undefined;

export const getNumericValueFromTextValue = (text) => {
  const newText = text.replace(/,/g, '.');
  const numbers = newText.match(/[+-]?\d+(\.\d+)?/g);
  return numbers ? getNumericFromText(numbers[0]) : undefined;
};

export const getConjointNumericValue = (text, attributeType) => {
  if (!text || attributeType !== CONJOINT_ATTRIBUTE_TYPES.PRICE) {
    return undefined;
  }

  return getNumericValueFromTextValue(text);
};

export const getAttributeValuesLength = (attributes) =>
  attributes.reduce(
    (accumulator, { values }) => accumulator + values.length,
    0,
  );
